import { HashiBridgeKey } from '../interfaces/hashi-bridge-key';
import { HashiIsland } from '../interfaces/hashi-island';
import {
  HashiBridgesMap,
  createsCrossing,
  computeNeighborPairs,
  normalizeKey,
  raycast,
  serializeKey,
} from '../geometry/hashi-geometry';

export interface HashiPoint {
  x: number;
  y: number;
}

export type HashiPointerIntent =
  | { kind: 'none' }
  | { kind: 'preview'; key: HashiBridgeKey }
  | { kind: 'commit'; key: HashiBridgeKey }
  | { kind: 'cancel' };

export interface HashiPointerContext {
  islands: HashiIsland[];
  bridges: HashiBridgesMap;
  bridgeKeys?: HashiBridgeKey[];
  islandCenter: (island: HashiIsland) => HashiPoint;
  now?: number;
}

export interface HashiPointerSnapshot {
  draggingFrom: HashiIsland | null;
  dragPreviewKey: string | null;
  hoveredBridgeKey: string | null;
  hoveredIslandId: string | null;
  touchHoverIslandId: string | null;
  isTouchDragging: boolean;
  isTouchActive: boolean;
}

export function computeTouchDragThreshold(cellSize: number): number {
  const relative = 0.2 * cellSize;
  return Math.min(Math.max(relative, 8), Math.max(24, 0.35 * cellSize));
}

export class HashiPointerController {
  private draggingFrom: HashiIsland | null = null;
  private hoveredKeyS: string | null = null;
  private hoveredIslandId: string | null = null;
  private dragPreviewKeyS: string | null = null;
  private touchHoverIslandId: string | null = null;

  private isTouchActive = false;
  private touchSuppressHoverUntil = 0;
  private isTouchDragging = false;
  private touchStartSvg: HashiPoint | null = null;
  private touchDragThreshold = 12;
  private dragOrientation: 'h' | 'v' | null = null;
  private touchStateDebounceUntil = 0;

  private cellSize = 60;

  setCellSize(cellSize: number): void {
    this.cellSize = cellSize;
    this.touchDragThreshold = computeTouchDragThreshold(cellSize);
  }

  snapshot(): HashiPointerSnapshot {
    return {
      draggingFrom: this.draggingFrom,
      dragPreviewKey: this.dragPreviewKeyS,
      hoveredBridgeKey: this.hoveredKeyS,
      hoveredIslandId: this.hoveredIslandId,
      touchHoverIslandId: this.touchHoverIslandId,
      isTouchDragging: this.isTouchDragging,
      isTouchActive: this.isTouchActive,
    };
  }

  isSuppressingHover(now: number = Date.now()): boolean {
    return this.isTouchActive || now < this.touchSuppressHoverUntil;
  }

  onMouseDownIsland(island: HashiIsland): void {
    this.draggingFrom = island;
  }

  onEnterBridge(key: HashiBridgeKey, now: number = Date.now()): void {
    if (this.isSuppressingHover(now)) return;
    this.hoveredKeyS = serializeKey(key);
  }

  onLeaveBridge(): void {
    this.hoveredKeyS = null;
  }

  onEnterIsland(island: HashiIsland, now: number = Date.now()): void {
    if (this.isSuppressingHover(now)) return;
    this.hoveredIslandId = island.id;
  }

  onLeaveIsland(): void {
    this.hoveredIslandId = null;
  }

  onMouseMove(svg: HashiPoint, ctx: HashiPointerContext): HashiPointerIntent {
    if (!this.draggingFrom) {
      this.dragPreviewKeyS = null;
      return { kind: 'none' };
    }
    const target = this.pickRaycastTarget(this.draggingFrom, svg, ctx, null);
    if (!target) {
      this.dragPreviewKeyS = null;
      return { kind: 'none' };
    }
    const k = normalizeKey(ctx.islands, { a: this.draggingFrom.id, b: target.id });
    if (!k) {
      this.dragPreviewKeyS = null;
      return { kind: 'none' };
    }
    if (this.previewWouldCross(k, ctx)) {
      this.dragPreviewKeyS = null;
      return { kind: 'none' };
    }
    this.dragPreviewKeyS = serializeKey(k);
    return { kind: 'preview', key: k };
  }

  onMouseUp(svg: HashiPoint, ctx: HashiPointerContext): HashiPointerIntent {
    let intent: HashiPointerIntent = { kind: 'none' };
    if (this.draggingFrom) {
      const target = this.pickRaycastTarget(this.draggingFrom, svg, ctx, null);
      if (target) {
        intent = { kind: 'commit', key: { a: this.draggingFrom.id, b: target.id } };
      }
    }
    this.draggingFrom = null;
    this.dragPreviewKeyS = null;
    return intent;
  }

  onMouseUpIsland(island: HashiIsland): HashiPointerIntent {
    if (!this.draggingFrom) return { kind: 'none' };
    const from = this.draggingFrom;
    this.draggingFrom = null;
    if (from.id === island.id) return { kind: 'none' };
    return { kind: 'commit', key: { a: from.id, b: island.id } };
  }

  onTouchStartIsland(island: HashiIsland, svg: HashiPoint | null, now: number = Date.now()): void {
    this.isTouchActive = true;
    this.touchSuppressHoverUntil = now + 700;
    this.hoveredKeyS = null;
    this.hoveredIslandId = null;
    this.dragPreviewKeyS = null;
    this.draggingFrom = island;
    this.isTouchDragging = false;
    this.dragOrientation = null;
    this.touchStateDebounceUntil = 0;
    this.touchDragThreshold = computeTouchDragThreshold(this.cellSize);
    this.touchHoverIslandId = island.id;
    this.touchStartSvg = svg ? { x: svg.x, y: svg.y } : null;
  }

  onTouchMove(svg: HashiPoint, ctx: HashiPointerContext, multiTouch = false): HashiPointerIntent {
    if (!this.draggingFrom) return { kind: 'none' };
    const now = ctx.now ?? Date.now();
    this.isTouchActive = true;

    if (multiTouch) {
      this.cancelTouch(true, now);
      return { kind: 'cancel' };
    }

    if (!this.isTouchDragging && this.touchStartSvg) {
      const dx0 = svg.x - this.touchStartSvg.x;
      const dy0 = svg.y - this.touchStartSvg.y;
      const dist = Math.hypot(dx0, dy0);
      if (dist < this.touchDragThreshold) {
        this.dragPreviewKeyS = null;
        return { kind: 'none' };
      }
      if (now >= this.touchStateDebounceUntil) {
        this.isTouchDragging = true;
        this.touchHoverIslandId = null;
        this.dragOrientation = this.chooseDragOrientation(this.draggingFrom, dx0, dy0, ctx.islands);
        this.touchStateDebounceUntil = now + 120;
      }
    }

    const from = this.draggingFrom;
    const c = ctx.islandCenter(from);

    if (this.isTouchDragging && this.touchStartSvg) {
      const distToCenter = Math.hypot(svg.x - c.x, svg.y - c.y);
      const distToStart = Math.hypot(svg.x - this.touchStartSvg.x, svg.y - this.touchStartSvg.y);
      const returnThreshold = this.touchDragThreshold * 0.55;
      if (Math.min(distToCenter, distToStart) < returnThreshold && now >= this.touchStateDebounceUntil) {
        this.isTouchDragging = false;
        this.dragPreviewKeyS = null;
        this.touchHoverIslandId = from.id;
        this.dragOrientation = null;
        this.touchStateDebounceUntil = now + 120;
        return { kind: 'none' };
      }
    }

    const target = this.pickRaycastTarget(from, svg, ctx, this.dragOrientation);
    if (!target) {
      // keep existing preview to avoid blink
      return { kind: 'none' };
    }
    const k = normalizeKey(ctx.islands, { a: from.id, b: target.id });
    if (!k) return { kind: 'none' };
    if (this.previewWouldCross(k, ctx)) {
      return { kind: 'none' };
    }
    this.dragPreviewKeyS = serializeKey(k);
    return { kind: 'preview', key: k };
  }

  onTouchEnd(svg: HashiPoint | null, ctx: HashiPointerContext): HashiPointerIntent {
    let intent: HashiPointerIntent = { kind: 'none' };
    if (this.draggingFrom && this.isTouchDragging && svg) {
      const target = this.pickRaycastTarget(this.draggingFrom, svg, ctx, this.dragOrientation);
      if (target) {
        intent = { kind: 'commit', key: { a: this.draggingFrom.id, b: target.id } };
      }
    }
    this.draggingFrom = null;
    this.dragPreviewKeyS = null;
    this.isTouchDragging = false;
    this.dragOrientation = null;
    this.isTouchActive = false;
    this.touchSuppressHoverUntil = (ctx.now ?? Date.now()) + 700;
    return intent;
  }

  onTouchCancel(now: number = Date.now()): void {
    this.cancelTouch(false, now);
  }

  onCycleBridgeRequested(key: HashiBridgeKey): HashiPointerIntent {
    this.hoveredKeyS = null;
    this.hoveredIslandId = null;
    this.dragPreviewKeyS = null;
    this.touchHoverIslandId = null;
    return { kind: 'commit', key };
  }

  private cancelTouch(keepTapPreview: boolean, now: number): void {
    this.draggingFrom = null;
    this.dragPreviewKeyS = null;
    this.isTouchDragging = false;
    this.isTouchActive = false;
    this.dragOrientation = null;
    this.touchSuppressHoverUntil = now + 300;
    if (!keepTapPreview) this.touchHoverIslandId = null;
  }

  private pickRaycastTarget(
    from: HashiIsland,
    svg: HashiPoint,
    ctx: HashiPointerContext,
    lockedOrientation: 'h' | 'v' | null
  ): HashiIsland | null {
    const c = ctx.islandCenter(from);
    const dx = svg.x - c.x;
    const dy = svg.y - c.y;
    if (lockedOrientation === 'h') {
      return raycast(ctx.islands, from, dx >= 0 ? 1 : -1, 0);
    }
    if (lockedOrientation === 'v') {
      return raycast(ctx.islands, from, 0, dy >= 0 ? 1 : -1);
    }
    if (Math.abs(dx) >= Math.abs(dy)) {
      return raycast(ctx.islands, from, dx >= 0 ? 1 : -1, 0);
    }
    return raycast(ctx.islands, from, 0, dy >= 0 ? 1 : -1);
  }

  private chooseDragOrientation(
    from: HashiIsland,
    dx0: number,
    dy0: number,
    islands: HashiIsland[]
  ): 'h' | 'v' {
    const preferH = Math.abs(dx0) >= Math.abs(dy0);
    const hasH = !!(raycast(islands, from, 1, 0) || raycast(islands, from, -1, 0));
    const hasV = !!(raycast(islands, from, 0, 1) || raycast(islands, from, 0, -1));
    if (preferH && hasH) return 'h';
    if (!preferH && hasV) return 'v';
    if (hasH) return 'h';
    return 'v';
  }

  private previewWouldCross(k: HashiBridgeKey, ctx: HashiPointerContext): boolean {
    const sk = serializeKey(k);
    const current = ctx.bridges[sk] ?? 0;
    const next = (current + 1) % 3;
    if (next <= current) return false;
    const bridgeKeys = ctx.bridgeKeys ?? computeNeighborPairs(ctx.islands);
    return createsCrossing(ctx.islands, bridgeKeys, ctx.bridges, k, next);
  }
}
